import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  Filter,
  Mail,
  Phone,
  Edit,
  Trash2,
  UserPlus,
  Download,
  ChevronDown,
  Shield,
  X,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const ContractorsScreen = () => {
  const { contractors, addContractor, deleteContractor } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [newContractor, setNewContractor] = useState({
    name: '',
    email: '',
    phone: '',
    shirtSize: '',
    primaryRole: '',
    notes: '',
  });

  const handleOpenModal = (contractor = null) => {
    if (contractor) {
      setSelectedContractor(contractor);
      setNewContractor({
        name: contractor.name || '',
        email: contractor.email || '',
        phone: contractor.phone || '',
        shirtSize: contractor.shirtSize || '',
        primaryRole: contractor.primaryRole || '',
        notes: contractor.notes || '',
      });
    } else {
      setSelectedContractor(null);
      setNewContractor({
        name: '',
        email: '',
        phone: '',
        shirtSize: '',
        primaryRole: '',
        notes: '',
      });
    }
    setShowAddModal(true);
  };

  const handleSaveContractor = () => {
    if (!newContractor.name || !newContractor.email) {
      alert('Name and email are required');
      return;
    }

    addContractor(newContractor);
    setShowAddModal(false);
    setSelectedContractor(null);
    setNewContractor({
      name: '',
      email: '',
      phone: '',
      shirtSize: '',
      primaryRole: '',
      notes: '',
    });
  };

  const filteredContractors = contractors
    .filter((contractor) =>
      !searchTerm
        ? true
        : contractor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contractor.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = (a[sortBy] || '').toString().toLowerCase();
      const bVal = (b[sortBy] || '').toString().toLowerCase();
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-lg p-6 border border-ops-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-ops-text-primary flex items-center gap-2">
              <Users className="w-6 h-6 text-ops-accent-primary" />
              Contractor Database
            </h2>
            <p className="text-ops-text-secondary mt-1">
              Maintain your roster of camera ops, utilities, A2s, engineers, and support crew.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center">
              <Shield className="w-4 h-4 text-ops-accent-primary mr-2" />
              <span className="text-xs text-ops-text-tertiary uppercase tracking-wide">
                Internal Use · Ops Only
              </span>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center px-3 py-2 rounded-md bg-ops-accent-primary text-ops-bg-primary text-sm font-medium hover:bg-ops-accent-secondary transition-colors"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Contractor
            </button>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="mt-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 text-ops-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 pr-3 py-2 w-full rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary placeholder:text-ops-text-tertiary focus:outline-none focus:ring-1 focus:ring-ops-accent-primary focus:border-ops-accent-primary text-sm"
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => toggleSort('name')}
              className="inline-flex items-center px-3 py-2 rounded-md border border-ops-border bg-ops-bg-card text-xs text-ops-text-secondary hover:border-ops-accent-primary hover:text-ops-text-primary transition-colors"
            >
              <span>Name</span>
              <ChevronDown className="w-3 h-3 ml-1" />
            </button>
            <button
              onClick={() => toggleSort('primaryRole')}
              className="inline-flex items-center px-3 py-2 rounded-md border border-ops-border bg-ops-bg-card text-xs text-ops-text-secondary hover:border-ops-accent-primary hover:text-ops-text-primary transition-colors"
            >
              <span>Role</span>
              <Filter className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Contractors Table */}
      <div className="glass rounded-lg p-4 md:p-6 border border-ops-border">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wide text-ops-text-tertiary">
            {filteredContractors.length} Contractors
          </div>
          <button className="inline-flex items-center px-2 py-1 rounded-md border border-ops-border text-xs text-ops-text-tertiary hover:border-ops-accent-primary hover:text-ops-accent-primary transition-colors">
            <Download className="w-3 h-3 mr-1" />
            Export CSV (coming soon)
          </button>
        </div>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="min-w-full divide-y divide-ops-border text-sm">
            <thead className="bg-ops-bg-elevated">
              <tr>
                <th
                  className="px-4 md:px-6 py-3 text-left text-xs font-medium text-ops-text-tertiary uppercase tracking-wider cursor-pointer"
                  onClick={() => toggleSort('name')}
                >
                  Name
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-ops-text-tertiary uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-ops-text-tertiary uppercase tracking-wider">
                  Phone
                </th>
                <th
                  className="px-4 md:px-6 py-3 text-left text-xs font-medium text-ops-text-tertiary uppercase tracking-wider cursor-pointer"
                  onClick={() => toggleSort('primaryRole')}
                >
                  Role
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-ops-text-tertiary uppercase tracking-wider">
                  Shirt
                </th>
                <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-ops-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ops-border">
              {filteredContractors.map((contractor) => (
                <tr key={contractor.id} className="hover:bg-ops-bg-elevated/40">
                  <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-ops-bg-card flex items-center justify-center mr-3 border border-ops-border">
                        <span className="text-xs font-medium text-ops-text-secondary">
                          {contractor.name
                            .split(' ')
                            .filter(Boolean)
                            .map((n) => n[0]?.toUpperCase())
                            .slice(0, 2)
                            .join('')}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-ops-text-primary">
                          {contractor.name}
                        </div>
                        <div className="text-xs text-ops-text-tertiary">
                          {contractor.primaryRole || 'Unassigned'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center text-ops-text-secondary">
                      <Mail className="w-3 h-3 mr-1.5 text-ops-text-tertiary" />
                      <span className="truncate max-w-[160px] md:max-w-xs">{contractor.email}</span>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center text-ops-text-secondary">
                      <Phone className="w-3 h-3 mr-1.5 text-ops-text-tertiary" />
                      <span>{contractor.phone || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-ops-bg-card border border-ops-border text-ops-text-secondary">
                      <Users className="w-3 h-3 mr-1 text-ops-accent-primary" />
                      {contractor.primaryRole || 'Crew'}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-ops-bg-card border border-ops-border text-ops-text-secondary">
                      {contractor.shirtSize || '—'}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(contractor)}
                        className="p-1.5 rounded hover:bg-ops-bg-card transition-colors"
                      >
                        <Edit className="w-4 h-4 text-ops-accent-primary" />
                      </button>
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              `Remove ${contractor.name} from the database? This will also remove them from any assignments.`
                            )
                          ) {
                            deleteContractor(contractor.id);
                          }
                        }}
                        className="p-1.5 rounded hover:bg-ops-bg-card transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-ops-accent-danger" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredContractors.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-10 text-center text-ops-text-tertiary text-sm"
                  >
                    No contractors found. Try adjusting your search or add your first contractor.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Contractor Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="glass rounded-lg border border-ops-border max-w-lg w-full mx-4 bg-ops-bg-primary"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-ops-border">
                <div>
                  <h3 className="text-lg font-semibold text-ops-text-primary flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-ops-accent-primary" />
                    {selectedContractor ? 'Edit Contractor' : 'Add Contractor'}
                  </h3>
                  <p className="text-xs text-ops-text-tertiary">
                    Contractors are matched by email address when importing forms.
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1 rounded hover:bg-ops-bg-card transition-colors"
                >
                  <X className="w-4 h-4 text-ops-text-tertiary" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-ops-text-tertiary mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={newContractor.name}
                      onChange={(e) =>
                        setNewContractor({ ...newContractor, name: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-ops-accent-primary"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ops-text-tertiary mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newContractor.email}
                      onChange={(e) =>
                        setNewContractor({ ...newContractor, email: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-ops-accent-primary"
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-ops-text-tertiary mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={newContractor.phone}
                      onChange={(e) =>
                        setNewContractor({ ...newContractor, phone: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-ops-accent-primary"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ops-text-tertiary mb-1">
                      Shirt Size
                    </label>
                    <input
                      type="text"
                      value={newContractor.shirtSize}
                      onChange={(e) =>
                        setNewContractor({
                          ...newContractor,
                          shirtSize: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-ops-accent-primary"
                      placeholder="M / L / XL"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ops-text-tertiary mb-1">
                      Primary Role
                    </label>
                    <input
                      type="text"
                      value={newContractor.primaryRole}
                      onChange={(e) =>
                        setNewContractor({
                          ...newContractor,
                          primaryRole: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-ops-accent-primary"
                      placeholder="Camera / UT / A2 / EIC"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-ops-text-tertiary mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newContractor.notes}
                    onChange={(e) =>
                      setNewContractor({ ...newContractor, notes: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-ops-accent-primary min-h-[80px]"
                    placeholder="Certifications, truck preferences, travel notes…"
                  />
                </div>
              </div>

              <div className="px-5 py-4 border-t border-ops-border flex items-center justify-between">
                <span className="text-xs text-ops-text-tertiary">
                  Contractors with matching emails will be merged when importing forms.
                </span>
                <button
                  onClick={handleSaveContractor}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-ops-accent-primary text-ops-bg-primary text-sm font-medium hover:bg-ops-accent-secondary transition-colors"
                >
                  {selectedContractor ? 'Update' : 'Add'} Contractor
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractorsScreen;
